const CONSENT_KEY = "thenea_consent";
const CONSENT_MAX_AGE_MS = 6 * 30 * 24 * 60 * 60 * 1000; // 6 months

export interface ConsentPreferences {
  analytics: boolean;
  marketing: boolean;
  decided: boolean;
  timestamp: number;
}

/* eslint-disable @typescript-eslint/no-explicit-any */
declare global {
  interface Window {
    dataLayer: unknown[];
    gtag: (...args: any[]) => void;
    fbq: any;
    _fbq: any;
    TiktokAnalyticsObject: string;
    ttq: any;
  }
}
/* eslint-enable @typescript-eslint/no-explicit-any */

const DEFAULTS: ConsentPreferences = {
  analytics: false,
  marketing: false,
  decided: false,
  timestamp: 0,
};

export function getConsent(): ConsentPreferences {
  try {
    const raw = localStorage.getItem(CONSENT_KEY);
    if (!raw) return { ...DEFAULTS };
    return { ...DEFAULTS, ...(JSON.parse(raw) as Partial<ConsentPreferences>) };
  } catch {
    return { ...DEFAULTS };
  }
}

export function needsConsentBanner(): boolean {
  const prefs = getConsent();
  if (!prefs.decided) return true;
  if (Date.now() - prefs.timestamp > CONSENT_MAX_AGE_MS) return true;
  return false;
}

export function saveConsent(analytics: boolean, marketing: boolean): ConsentPreferences {
  const prefs: ConsentPreferences = { analytics, marketing, decided: true, timestamp: Date.now() };
  try {
    localStorage.setItem(CONSENT_KEY, JSON.stringify(prefs));
  } catch { /* noop */ }
  return prefs;
}

// ── Google Consent Mode v2 update ────────────────────────────────────────────

function gtagUpdate(analytics: boolean, marketing: boolean) {
  if (typeof window === "undefined" || typeof window.gtag !== "function") return;
  window.gtag("consent", "update", {
    analytics_storage: analytics ? "granted" : "denied",
    ad_storage: marketing ? "granted" : "denied",
    ad_user_data: marketing ? "granted" : "denied",
    ad_personalization: marketing ? "granted" : "denied",
  });
}

// ── GA4 ──────────────────────────────────────────────────────────────────────

let ga4Loaded = false;
function loadGA4() {
  const id = import.meta.env.VITE_GA4_ID as string | undefined;
  if (!id || ga4Loaded || typeof window === "undefined") return;
  ga4Loaded = true;
  const s = document.createElement("script");
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${id}`;
  document.head.appendChild(s);
  window.gtag("js", new Date());
  window.gtag("config", id);
}

// ── Meta Pixel ───────────────────────────────────────────────────────────────

let metaLoaded = false;
function loadMetaPixel() {
  const id = import.meta.env.VITE_META_PIXEL_ID as string | undefined;
  if (!id || metaLoaded || typeof window === "undefined") return;
  metaLoaded = true;
  if (!window.fbq) {
    const n: any = function (...args: any[]) {
      if (n.callMethod) {
        n.callMethod(...args);
      } else {
        n.queue.push(args);
      }
    };
    n.push = n;
    n.loaded = true;
    n.version = "2.0";
    n.queue = [];
    window.fbq = n;
    window._fbq = n;
  }
  const s = document.createElement("script");
  s.async = true;
  s.src = "https://connect.facebook.net/en_US/fbevents.js";
  document.head.appendChild(s);
  window.fbq("init", id);
  window.fbq("track", "PageView");
}

// ── TikTok Pixel ─────────────────────────────────────────────────────────────

let tiktokLoaded = false;
function loadTikTokPixel() {
  const id = import.meta.env.VITE_TIKTOK_PIXEL_ID as string | undefined;
  if (!id || tiktokLoaded || typeof window === "undefined") return;
  tiktokLoaded = true;
  const w = window as any;
  w.TiktokAnalyticsObject = "ttq";
  const ttq = (w.ttq = w.ttq || []);
  ttq.methods = ["page","track","identify","instances","debug","on","off","once","ready","alias","group","enableCookie","disableCookie"];
  ttq.setAndDefer = (obj: any, method: string) => {
    obj[method] = (...args: any[]) => { obj.push([method, ...args]); };
  };
  ttq.methods.forEach((m: string) => ttq.setAndDefer(ttq, m));
  ttq.instance = (name: string) => {
    const i = w.ttq._i[name] || [];
    ttq.methods.forEach((m: string) => ttq.setAndDefer(i, m));
    return i;
  };
  ttq.load = (pixelId: string) => {
    const src = "https://analytics.tiktok.com/i18n/pixel/events.js";
    ttq._i = ttq._i || {};
    ttq._i[pixelId] = [];
    ttq._t = ttq._t || {};
    ttq._t[pixelId] = +new Date();
    ttq._o = ttq._o || {};
    const s = document.createElement("script");
    s.async = true;
    s.src = `${src}?sdkid=${pixelId}&lib=ttq`;
    document.head.appendChild(s);
  };
  ttq.load(id);
  ttq.page();
}

// ── Google Ads ────────────────────────────────────────────────────────────────

let gAdsConfigured = false;
function loadGoogleAds() {
  const id = import.meta.env.VITE_GOOGLE_ADS_ID as string | undefined;
  if (!id || gAdsConfigured || typeof window === "undefined" || typeof window.gtag !== "function") return;
  gAdsConfigured = true;
  window.gtag("config", id);
}

// ── Public API ────────────────────────────────────────────────────────────────

export function applyConsent(prefs: ConsentPreferences) {
  gtagUpdate(prefs.analytics, prefs.marketing);
  loadGA4();
  if (prefs.marketing) {
    loadMetaPixel();
    loadTikTokPixel();
    loadGoogleAds();
  }
}

export function initConsent() {
  applyConsent(getConsent());
}
